import { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Plus, Trash2 } from 'lucide-react';
import { ApiError } from '../../../lib/api';
import {
  useSetTrainingNotify,
  type TrainingModuleSummary,
} from '../../../hooks/useTrainingModules';
import { Button } from '../../ui/Button';
import { SHARED_INPUT_CLASS } from './pageMeta';

const MAX_EMAILS = 10;

/**
 * "Email the answers when someone completes this": a tick box and the list of
 * addresses to send to. Saved straight to the module the moment Save is
 * pressed, not on publish — who gets notified is an operational setting, not
 * course content, so changing it must never mint a new version.
 */
export function NotifyAnswersCard({ module }: { module: TrainingModuleSummary }) {
  const save = useSetTrainingNotify();
  const [on, setOn] = useState(module.notify_on_completion);
  const [emails, setEmails] = useState<string[]>(
    module.notify_emails.length > 0 ? module.notify_emails : [''],
  );

  const filled = emails.map((e) => e.trim()).filter((e) => e !== '');
  const dirty =
    on !== module.notify_on_completion ||
    filled.join(',') !== module.notify_emails.join(',');

  async function onSave() {
    try {
      await save.mutateAsync({
        id: module.id,
        notify_on_completion: on && filled.length > 0,
        notify_emails: filled,
      });
      toast.success(on && filled.length > 0 ? 'Notifications saved' : 'Notifications off');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save notifications');
    }
  }

  return (
    <div className="rounded-token border border-line/10 bg-surface p-3">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-fg-muted" />
        <span className="text-sm font-medium text-fg">When someone completes this module</span>
      </div>
      <label className="mt-2 flex items-start gap-2 text-sm text-fg">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={on}
          onChange={(e) => setOn(e.target.checked)}
        />
        <span>
          <span className="font-medium">Email me their answers.</span>{' '}
          <span className="text-fg-muted">
            The answers are attached as a PDF, one email per address below.
          </span>
        </span>
      </label>
      {on ? (
        <div className="mt-3 space-y-2">
          {emails.map((email, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) =>
                  setEmails((list) => list.map((v, x) => (x === i ? e.target.value : v)))
                }
                placeholder="name@corrigenda.co.uk"
                className={`${SHARED_INPUT_CLASS} mt-0 flex-1`}
              />
              <button
                type="button"
                aria-label="Remove address"
                disabled={emails.length <= 1}
                onClick={() => setEmails((list) => list.filter((_, x) => x !== i))}
                className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            iconLeft={<Plus className="h-4 w-4" />}
            disabled={emails.length >= MAX_EMAILS}
            onClick={() => setEmails((list) => [...list, ''])}
          >
            Add address
          </Button>
          <p className="text-xs text-fg-dim">
            The PDF contains the learner's personal data. Only add people who need to see it.
          </p>
        </div>
      ) : null}
      <div className="mt-3 flex items-center gap-2 border-t border-line/10 pt-3">
        <Button
          variant="secondary"
          size="sm"
          disabled={!dirty}
          loading={save.isPending}
          onClick={() => void onSave()}
        >
          Save
        </Button>
        <span className="text-xs text-fg-dim">
          {dirty ? 'Not saved yet' : 'Saved separately from the module version'}
        </span>
      </div>
    </div>
  );
}
